import {Component, EventEmitter, inject, Input, OnInit, Output} from '@angular/core';
import {DropdownValue} from '../../../../../../utilities/DropdownComponent';
import {FormBuilder, FormControl, Validators} from '@angular/forms';
import {
    AddAssistanceApplicationStateAuditViewModel, ApplicationDenialData,
    AssistanceApplicationDenialReason, AssistanceApplicationStateAuditType
} from '../../../../../../services/api.client';
import {AssistanceApplicationStateAuditService} from '../../../../../../services/assistanceApplicationStateAudit.service';
import {debounceTime, filter, mergeMap, takeUntil} from "rxjs/operators";
import {Disposable} from "../../../../../../utilities/Disposable";
import {BehaviorSubject, of} from "rxjs";
import {
    ASSISTANCE_APPLICATION_DENIAL_REASON_OPTIONS
} from "../../../../../../shared/constants/options/assistance-application-denial-options";
import {AssistanceApplicationReviewService} from "../../../../../../services/assistance-application-review.service";
import {NotificationService} from "../../../../../../services/NotificationService";
import {getMessageFromException} from "../../../../../../utilities/Util";

@Component({
    selector: 'app-manage-audit-state',
    templateUrl: './manage-audit-state.component.html',
    styleUrls: ['./manage-audit-state.component.scss'],
    standalone: false
})
export class ManageAuditStateComponent extends Disposable implements OnInit {

    @Input() assistanceApplicationId;
    @Output() savedAuditState = new EventEmitter<AddAssistanceApplicationStateAuditViewModel>();
    @Output() closeModal = new EventEmitter();

    private _notificationService = inject(NotificationService);
    private _fb = inject(FormBuilder);
    private _assistanceApplicationStateAuditService = inject(AssistanceApplicationStateAuditService);
    private _currentAuditState: string;
    protected readonly ASSISTANCE_APPLICATION_DENIAL_REASON_OPTIONS = ASSISTANCE_APPLICATION_DENIAL_REASON_OPTIONS;
    protected readonly AssistanceApplicationDenialReason = AssistanceApplicationDenialReason;
    protected isFailedAuditState$ = new BehaviorSubject<boolean>(false);
    public isProcessing = false;

    constructor(private _reviewService: AssistanceApplicationReviewService) {
        super();
    }

    form = this._fb.group({
        auditState: new FormControl('', Validators.required),
        auditNote: new FormControl('', Validators.compose([Validators.required, Validators.maxLength(512)])),
        denialReason: new FormControl<AssistanceApplicationDenialReason>(null),
        otherDenialReason: new FormControl('', Validators.maxLength(128))
    });

    private _savedAuditState: AddAssistanceApplicationStateAuditViewModel = new AddAssistanceApplicationStateAuditViewModel();

    auditStates: DropdownValue[] = [
        new DropdownValue(AssistanceApplicationStateAuditType[AssistanceApplicationStateAuditType.NotYetAudited.toString()], 'Not Yet Audited'),
        new DropdownValue(AssistanceApplicationStateAuditType[AssistanceApplicationStateAuditType.Pass.toString()], 'Pass'),
        new DropdownValue(AssistanceApplicationStateAuditType[AssistanceApplicationStateAuditType.PassWithCorrection.toString()], 'Pass With Correction'),
        new DropdownValue(AssistanceApplicationStateAuditType[AssistanceApplicationStateAuditType.Fail.toString()], 'Fail'),
    ];

    get auditState(): FormControl {
        return this.form?.get('auditState') as FormControl;
    }

    get auditNote(): FormControl {
        return this.form?.get('auditNote') as FormControl;
    }

    get denialReason(): FormControl<AssistanceApplicationDenialReason> {
        return this.form?.get('denialReason') as FormControl<AssistanceApplicationDenialReason>;
    }

    get otherDenialReason(): FormControl {
        return this.form?.get('otherDenialReason') as FormControl;
    }

    ngOnInit(): void {
        this._assistanceApplicationStateAuditService.retrieveCaseCurrentState(this.assistanceApplicationId).subscribe(result => {
            this._currentAuditState = result?.newState
                ? result?.newState?.toString()
                : result?.oldState?.toString() || AssistanceApplicationStateAuditType.NotYetAudited.toString();

            const initialAuditValue = this.auditStates.find(x => x.id === this._currentAuditState);
            this.auditState.setValue(initialAuditValue?.id);
            this._savedAuditState = AddAssistanceApplicationStateAuditViewModel.fromJS({
                assistanceApplicationId: this.assistanceApplicationId,
                newState: this.getAuditState(this._currentAuditState),
                note: '',
                denialData: ApplicationDenialData.fromJS({
                    denialReason: this.getDenialReason(this.denialReason?.value?.toString()),
                    otherReason: this.otherDenialReason?.value
                })
            });
        });

        this.auditState.valueChanges.pipe(
            takeUntil(this.$destroyed),
        ).subscribe(value => {
            this.isFailedAuditState$.next(value === AssistanceApplicationStateAuditType[AssistanceApplicationStateAuditType.Fail.toString()]);
            if (value !== AssistanceApplicationStateAuditType[AssistanceApplicationStateAuditType.Fail.toString()]) {
                this.denialReason.setValue(null);
                this.otherDenialReason.setValue('');
                this.denialReason.clearValidators();
                this.otherDenialReason.clearValidators();
            } else {
                this.denialReason.setValidators(Validators.required);
            }
            this.denialReason.updateValueAndValidity();
            this.otherDenialReason.updateValueAndValidity();
        });

        this.denialReason.valueChanges.pipe(
            takeUntil(this.$destroyed)
        ).subscribe(value => {
            if (value === AssistanceApplicationDenialReason.Other) {
                this.otherDenialReason.setValidators(Validators.required);
            } else {
                this.otherDenialReason.setValue('');
                this.otherDenialReason.clearValidators();
            }
            this.otherDenialReason.updateValueAndValidity();
            this._savedAuditState = AddAssistanceApplicationStateAuditViewModel.fromJS({
                assistanceApplicationId: this.assistanceApplicationId,
                newState: this._savedAuditState?.newState,
                note: this.auditNote?.value,
                denialData: ApplicationDenialData.fromJS({
                    denialReason: this.getDenialReason(this.denialReason?.value?.toString()),
                    otherReason: this.otherDenialReason?.value
                })
            });
            this.savedAuditState.emit(this._savedAuditState);
        });

        this.otherDenialReason.valueChanges.pipe(
            takeUntil(this.$destroyed),
            debounceTime(500)
        ).subscribe(() => {
            this._savedAuditState = AddAssistanceApplicationStateAuditViewModel.fromJS({
                assistanceApplicationId: this.assistanceApplicationId,
                newState: this._savedAuditState?.newState,
                note: this.auditNote?.value,
                denialData: ApplicationDenialData.fromJS({
                    denialReason: this.getDenialReason(this.denialReason?.value?.toString()),
                    otherReason: this.otherDenialReason?.value
                })
            });
            this.savedAuditState.emit(this._savedAuditState);
        }
        );

    }

    onSelectedAuditState(event): void {
        this._savedAuditState = AddAssistanceApplicationStateAuditViewModel.fromJS({
            assistanceApplicationId: this.assistanceApplicationId,
            newState: this.getAuditState(event?.target.value),
            note: this.auditNote?.value,
            denialData: ApplicationDenialData.fromJS({
                denialReason: this.getDenialReason(this.denialReason?.value?.toString()),
                otherReason: this.otherDenialReason?.value
            })
        });
        this.savedAuditState.emit(this._savedAuditState);
    }

    onChangeAuditNote(): void {
        this._savedAuditState = AddAssistanceApplicationStateAuditViewModel.fromJS({
            assistanceApplicationId: this.assistanceApplicationId,
            newState: this._savedAuditState?.newState,
            note: this.auditNote?.value,
            denialData: ApplicationDenialData.fromJS({
                denialReason: this.getDenialReason(this.denialReason?.value?.toString()),
                otherReason: this.otherDenialReason?.value
            })
        });
        this.savedAuditState.emit(this._savedAuditState);
    }

    onClickCancelAuditState(): void {
        this.closeModal.emit();
        this.resetForm();
    }

    onClickConfirmAuditState(): void {
        if (this.isProcessing) {
            return;
        }
        if (!this.form.valid) {
            this.form.markAllAsTouched();
            return;
        }
        this.isProcessing = true;
        this._assistanceApplicationStateAuditService.addAssistanceApplicationStateAudit(this._savedAuditState)
            .subscribe({
                next: () => {
                    this.resetForm();
                    this.isProcessing = false;
                    this.closeModal.emit();
                    window.location.reload();
                },
                error: (err) => {
                    console.error('Failed to update audit state', err);
                    this.isProcessing = false;
                    this._notificationService.showError('Failed to update audit state.');
                }
            });
    }

    private getAuditState(enumStringValue: string): string {
        return AssistanceApplicationStateAuditType[enumStringValue];
    }

    private getDenialReason(enumStringValue: string): string {
        return AssistanceApplicationDenialReason[enumStringValue];
    }


    private resetForm(): void {
        this.auditNote.markAsUntouched();
        this.auditNote.setValue('');
    }
}
